'use client';
import {useId} from 'react';

import {TextField} from '@/modules/commerce/form/field/TextField';
import {RadioTile} from '@/modules/commerce/form/RadioTile';
import {RadioTileFrame} from '@/modules/commerce/form/RadioTileFrame';
import {Select} from '@/modules/commerce/form/Select';
import {ProductColorSelector} from '@/modules/commerce/product-color/ProductColorSelector';
import {ImageBlock} from '@/modules/foundation/components/image/ImageBlock';
import {Spinner} from '@/modules/foundation/components/spinner';
import {Heading} from '@/modules/foundation/components/typography/heading';
import {Paragraph} from '@/modules/foundation/components/typography/paragraph';
import {imageFromSanityImage} from '@/modules/foundation/sanity/image/query';

import {useDynamicFont} from '../../font/hooks';
import {ProductFormField, ProductFormHeading} from '../../ProductForm';
import type {
	StickerFragPrintElement,
	StickerFragTextElement,
	StickerPrintElementConfig,
	StickerTextElementConfig,
} from '../configuration/stickerElementConfiguration';
import {
	getSelectableStickerElements,
	getStickerOptionKey,
	isStickerFragPrintElement,
	isStickerFragTextElement,
	isStickerPrintElementConfig,
	isStickerTextElementConfig,
	STICKER_FONT_KEY,
	STICKER_PRINT_KEY,
	STICKER_TEXT_KEY,
} from '../configuration/stickerElementConfiguration';
import type {StickerLocation} from '../configuration/stickerLocationConfiguration';
import {
	getStickerLocationName,
	STICKER_LOCATION_INSIDE,
	STICKER_LOCATION_OUTSIDE,
} from '../configuration/stickerLocationConfiguration';
import type {StickerProductConfiguration} from '../configuration/stickerProductConfiguration';
import type {StickerProductFragment, StickerVariantFragment} from '../query';
import {OBJECT_TYPE_STICKER_MULTICOLOR_PRINT_ELEMENT} from '../schema/names';

export function StickerLocationSelector({
	fragment,
	defaultLocation,
}: {
	fragment: StickerProductFragment;
	defaultLocation: StickerLocation;
}) {
	if (!fragment.canSelectMountingSide) {
		return null;
	}

	const insideName = getStickerLocationName(STICKER_LOCATION_INSIDE);
	const outsideName = getStickerLocationName(STICKER_LOCATION_OUTSIDE);

	return (
		<ProductFormField>
			<ProductFormField>
				<fieldset className="mb-8">
					<ProductFormHeading>
						<legend>Monteringsside</legend>
					</ProductFormHeading>

					<div className="flex flex-wrap gap-4">
						<RadioTile
							id={outsideName}
							name="location"
							value={STICKER_LOCATION_OUTSIDE}
							defaultChecked={defaultLocation === STICKER_LOCATION_OUTSIDE}
						>
							{outsideName}
						</RadioTile>
						<RadioTile
							id={insideName}
							name="location"
							value={STICKER_LOCATION_INSIDE}
							defaultChecked={defaultLocation === STICKER_LOCATION_INSIDE}
						>
							{insideName}
						</RadioTile>
					</div>
				</fieldset>
			</ProductFormField>
		</ProductFormField>
	);
}

type StickerVariantSelectorProps = {
	variants: StickerVariantFragment[];
	defaultVariantId?: string | null;
};

export function StickerVariantSelector({variants, defaultVariantId}: StickerVariantSelectorProps) {
	const id = useId();
	if (variants.length === 1) {
		return <input type="hidden" name="variantId" value={variants[0]._key} />;
	}

	return (
		<fieldset className="mb-8">
			<Heading level={2} size="h3" className="mb-3 block" asChild>
				<label htmlFor={id}>Variant</label>
			</Heading>

			<Select id={id} name="variantId" defaultValue={defaultVariantId ?? undefined} size="md">
				{variants.map((variant, i) => {
					const name = variant.name ?? variant.items?.at(0)?.name;
					return (
						<option key={i} aria-label={variant._key} value={variant._key}>
							{name}
						</option>
					);
				})}
			</Select>
		</fieldset>
	);
}

export function StickerColorSelector({
	fragment,
	defaultColorId,
}: {
	fragment: StickerProductFragment;
	defaultColorId?: string | null;
}) {
	if (fragment.stickerType === 'multiColor') {
		return null;
	}

	const colors = fragment.colors;

	if (!colors || colors.length === 0) {
		return null;
	}

	return (
		<ProductFormField>
			<fieldset>
				<ProductFormHeading>
					<legend>Farge</legend>
				</ProductFormHeading>
				<ProductColorSelector
					namePrefix="colorId"
					colors={fragment.colors ?? []}
					defaultConfiguration={defaultColorId ?? null}
					disableNoneSelection
				/>
			</fieldset>
		</ProductFormField>
	);
}

function StickerFontInput({
	fontName,
	fontKey,
	fontUrl,
	elementId,
	configElement,
}: {
	fontName: string;
	fontKey: string;
	fontUrl: string;
	elementId: string;
	configElement: StickerTextElementConfig;
}) {
	const {isLoaded} = useDynamicFont({name: fontName, url: fontUrl});

	if (!isLoaded) {
		return <Spinner title={'laster...'} />;
	}

	return (
		<RadioTileFrame
			name={getStickerOptionKey(elementId, STICKER_FONT_KEY)}
			title={fontName}
			alt={fontName || ''}
			value={`${fontKey}`}
			className="sr-only"
			defaultChecked={elementId === configElement.id && fontKey === configElement.fontId}
			labelProps={{
				className: 'bg-preview-background p-2',
			}}
		>
			<div className="flex h-8 min-w-24 flex-row items-center justify-center p-2">
				<div style={{fontFamily: fontName}}>{fontName}</div>
			</div>
		</RadioTileFrame>
	);
}

function StickerTextSelector({
	fragElement,
	configElement,
}: {
	fragElement: StickerFragTextElement;
	configElement: StickerTextElementConfig;
}) {
	const {_key: elementId, collection} = fragElement;

	if (!collection || collection.fonts === null) {
		return null;
	}

	const {fonts} = collection;

	if (!fonts || fonts.length === 0) {
		return null;
	}

	return (
		<div>
			<ProductFormHeading>{fragElement.name}</ProductFormHeading>
			<div className="flex w-full flex-col gap-2 rounded-md bg-neutral-800 p-4">
				<fieldset className="mb-3 w-full">
					<Paragraph className="mb-2 font-semibold" asChild>
						<legend>Skrifttype</legend>
					</Paragraph>
					<div className="flex w-full max-w-full flex-row flex-wrap justify-start gap-2">
						{fonts.map(({_id: fontKey, name: fontName, file}, i) => {
							const url = file?.url;

							if (!fontName || !url) {
								return null;
							}

							return (
								<StickerFontInput
									key={i}
									elementId={elementId}
									configElement={configElement}
									fontKey={fontKey}
									fontName={fontName}
									fontUrl={url}
								/>
							);
						})}
					</div>
				</fieldset>

				<TextField
					type="text"
					label="Tekst"
					alt={'Teksten som skal vises på klistremerket.'}
					name={getStickerOptionKey(elementId, STICKER_TEXT_KEY)}
					maxLength={fragElement.maxLength ?? 30}
					defaultValue={configElement.text ?? ''}
					placeholder={fragElement.defaultText ?? ''}
					labelProps={{
						className: 'text-base lg:text-lg',
					}}
				/>
			</div>
		</div>
	);
}

// TODO: make a generic print selector
function StickerPrintSelector({
	fragElement,
	configElement,
}: {
	fragElement: StickerFragPrintElement;
	configElement: StickerPrintElementConfig;
}) {
	const {_key: elementId, collection} = fragElement;

	if (!collection || collection.prints === null) {
		return null;
	}

	const {prints} = collection;

	if (!prints || prints.length === 0) {
		return null;
	}

	return (
		<div>
			<ProductFormHeading>{fragElement.name}</ProductFormHeading>
			<div className="flex w-full flex-col gap-2 rounded-md bg-neutral-800 p-4">
				<fieldset>
					<Paragraph className="mb-2 font-semibold" asChild>
						<legend>{collection.name}</legend>
					</Paragraph>
					<div className="flex flex-row flex-wrap gap-2">
						{prints.map(({_key: printKey, name: printName, image: printImage}) => {
							const image = imageFromSanityImage(printImage);

							if (!image) {
								return null;
							}

							return (
								<RadioTileFrame
									key={printKey}
									title={printName ?? undefined}
									name={getStickerOptionKey(elementId, STICKER_PRINT_KEY)}
									value={`${printKey}`}
									defaultChecked={
										elementId === configElement.id &&
										printKey === configElement.printId
									}
									labelProps={{
										className: 'bg-preview-background p-2',
									}}
								>
									{fragElement._type ===
									OBJECT_TYPE_STICKER_MULTICOLOR_PRINT_ELEMENT ? (
										<ImageBlock
											{...image}
											alt={printName || ''}
											className="object-contain"
											style={{width: 72, height: 72}}
										/>
									) : (
										<div
											className="group-hover:bg-secondary-dm block size-18 bg-white mask-contain mask-center mask-no-repeat"
											style={{
												maskImage: `url(${image.src})`,
											}}
										/>
									)}
								</RadioTileFrame>
							);
						})}
					</div>
				</fieldset>
			</div>
		</div>
	);
}

export function StickerElementSelector({
	fragment,
	config,
}: {
	fragment: StickerProductFragment;
	config: StickerProductConfiguration;
}) {
	const fragElements = getSelectableStickerElements(fragment);

	return (
		fragElements.length > 0 && (
			<ProductFormField className="mb-8 flex flex-col flex-wrap">
				<div className="flex w-full flex-col flex-wrap gap-8">
					{fragElements.map((fragElement, i) => {
						const configElement = config.elements.find(
							(e) => e.id === fragElement._key,
						);

						if (!configElement) {
							return null;
						}

						// FIXME: This code disables the element selector if an invalid config is
						//        detected. This is not a good solution as invalid config _will_
						//        happen and should be supported. Invalid configs should be handled
						//        during validation instead.
						if (
							isStickerFragPrintElement(fragElement) &&
							isStickerPrintElementConfig(configElement)
						) {
							return (
								<StickerPrintSelector
									key={i}
									fragElement={fragElement}
									configElement={configElement}
								/>
							);
						}
						if (
							isStickerFragTextElement(fragElement) &&
							isStickerTextElementConfig(configElement)
						) {
							return (
								<StickerTextSelector
									key={i}
									fragElement={fragElement}
									configElement={configElement}
								/>
							);
						}
					})}
				</div>
			</ProductFormField>
		)
	);
}
